import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  CloudOff,
  LoaderCircle,
  Redo2,
  Scan,
  StickyNote,
  Undo2,
  WandSparkles,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { Button } from '../components/common/Button';
import { cn } from '../utils/cn';
import MindMapCanvas from '../components/mindmap/MindMapCanvas';
import MindMapToolbar from '../components/mindmap/MindMapToolbar';
import NodeEditor from '../components/mindmap/NodeEditor';
import ShapeToolbar from '../components/mindmap/ShapeToolbar';
import { useMindMapEditor } from '../components/mindmap/useMindMapEditor';
import { deriveTitle } from '../components/mindmap/tree';

const MOBILE_PANEL_HEIGHT = 350;
const MOBILE_KEYBOARD_HEIGHT = 300;

const IconButton = ({ icon: Icon, label, onClick, disabled, className }) => (
  <Button
    variant="ghost"
    size="icon"
    aria-label={label}
    title={label}
    onClick={onClick}
    disabled={disabled}
    className={cn('h-10 w-10 shrink-0', className)}
  >
    <Icon className="h-5 w-5" />
  </Button>
);

const SaveIndicator = ({ status }) => {
  if (status === 'saving' || status === 'dirty') {
    return (
      <span className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground" role="status">
        <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
        <span className="hidden sm:inline">Saving…</span>
      </span>
    );
  }
  if (status === 'error' || status === 'conflict') {
    return (
      <span className="flex items-center gap-1.5 px-1 text-xs text-destructive" role="status">
        <CloudOff className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{status === 'conflict' ? 'Not saved' : 'Not saved, retrying…'}</span>
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground" role="status">
      <Check className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">Saved</span>
    </span>
  );
};

const FullScreenMessage = ({ title, message, children }) => (
  <div className="fixed inset-0 flex flex-col items-center justify-center gap-3 bg-background p-6 text-center">
    <h1 className="text-xl font-semibold">{title}</h1>
    {message && <p className="max-w-sm text-sm text-muted-foreground">{message}</p>}
    <div className="flex gap-2">{children}</div>
  </div>
);

export default function MindMapEditorPage() {
  const { mindMapId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canvasRef = useRef(null);
  const openedNewMap = useRef(false);

  const editor = useMindMapEditor(mindMapId);
  const {
    loadState, nodes, shapes, selectedId, selectedShapeId, editing, saveState, canUndo, canRedo,
    select, selectShape, openEditor, openShapeEditor, closeEditor, addChild, addSibling, updateNode, setNodeColor,
    deleteNode, toggleDone, toggleCollapsed, addShape, updateShape, setShapeKind, deleteShape,
    undo, redo, tidy, flushSave, reload, overwrite,
  } = editor;

  const root = nodes.find((node) => node.parentId == null);
  const selectedNode = nodes.find((node) => node.id === selectedId);
  const selectedShape = shapes.find((shape) => shape.id === selectedShapeId);
  const editingNode = editing?.target === 'node';
  const panelNode = editingNode && editing.mode === 'panel' ? nodes.find((node) => node.id === editing.id) : null;
  const inlineEditingId = editingNode && editing.mode === 'inline' ? editing.id : null;
  const editingShapeId = editing?.target === 'shape' ? editing.id : null;
  const hasChildren = selectedNode && nodes.some((node) => node.parentId === selectedNode.id);

  useEffect(() => {
    if (loadState.status !== 'ready' || !root || openedNewMap.current) return;
    if (searchParams.get('new')) {
      openedNewMap.current = true;
      openEditor(root.id, 'inline');
      setSearchParams({}, { replace: true });
    }
  }, [loadState.status, root, searchParams, setSearchParams, openEditor]);

  const editingId = editing?.id;
  const editingMode = editing?.mode;
  useEffect(() => {
    if (editingId && window.matchMedia('(max-width: 767px)').matches) {
      canvasRef.current?.reveal(editingId, {
        top: editingMode === 'panel' ? MOBILE_PANEL_HEIGHT : 0,
        bottom: MOBILE_KEYBOARD_HEIGHT,
      });
    }
  }, [editingId, editingMode]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const target = event.target;
      if (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(target.tagName)) return;

      const modifier = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();
      if (modifier && key === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
        return;
      }
      if (modifier && key === 'y') {
        event.preventDefault();
        redo();
        return;
      }
      if (modifier || event.altKey) return;

      if (selectedShape) {
        if (event.key === 'Enter' || event.key === 'F2' || event.key === ' ') {
          event.preventDefault();
          openShapeEditor(selectedShape.id);
        } else if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault();
          deleteShape(selectedShape.id);
        } else if (event.key === 'Escape') {
          select(null);
        }
        return;
      }
      if (!selectedNode) return;

      const isRoot = selectedNode.parentId == null;
      if (event.key === 'Tab') {
        event.preventDefault();
        addChild(selectedNode.id);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (isRoot) addChild(selectedNode.id);
        else addSibling(selectedNode.id);
      } else if (event.key === 'F2' || event.key === ' ') {
        event.preventDefault();
        openEditor(selectedNode.id);
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        deleteNode(selectedNode.id);
      } else if (event.key === 'Escape') {
        select(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedNode, selectedShape, undo, redo, addChild, addSibling, openEditor, openShapeEditor, deleteNode, deleteShape, select]);

  const handleBack = useCallback(async () => {
    await flushSave();
    navigate('/mindmaps');
  }, [flushSave, navigate]);

  const handleTidy = useCallback(() => {
    tidy();
    setTimeout(() => canvasRef.current?.fit(), 340);
  }, [tidy]);

  if (loadState.status === 'loading') {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background" role="status" aria-label="Loading mind map">
        <LoaderCircle className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (loadState.status === 'notFound' || loadState.status === 'error') {
    const notFound = loadState.status === 'notFound';
    return (
      <FullScreenMessage
        title={notFound ? 'Mind map not found' : "Couldn't load this mind map"}
        message={notFound ? 'It may have been deleted.' : loadState.message}
      >
        <Button variant="outline" onClick={() => navigate('/mindmaps')}>All mind maps</Button>
        {!notFound && <Button onClick={reload}>Try again</Button>}
      </FullScreenMessage>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-background text-foreground">
      <header className="z-30 flex h-14 shrink-0 items-center gap-1 border-b border-border bg-background/90 px-2 backdrop-blur">
        <IconButton icon={ArrowLeft} label="Back to mind maps" onClick={handleBack} />
        <button
          type="button"
          onClick={() => root && openEditor(root.id, 'panel')}
          className="min-w-0 flex-1 truncate rounded-md px-2 py-1 text-left text-base font-semibold hover:bg-accent"
          title="Rename"
        >
          {deriveTitle(nodes)}
        </button>
        <SaveIndicator status={saveState.status} />
        <IconButton icon={Undo2} label="Undo" onClick={undo} disabled={!canUndo} />
        <IconButton icon={Redo2} label="Redo" onClick={redo} disabled={!canRedo} />
        <IconButton icon={WandSparkles} label="Tidy up layout" onClick={handleTidy} />
        <IconButton icon={Scan} label="Fit to screen" onClick={() => canvasRef.current?.fit()} />
      </header>

      {saveState.status === 'conflict' && (
        <div role="alert" className="z-30 flex flex-wrap items-center gap-2 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm">
          <span className="min-w-[12rem] flex-1">This mind map was changed somewhere else.</span>
          <Button size="sm" variant="outline" onClick={reload}>Load latest</Button>
          <Button size="sm" onClick={overwrite}>Keep my version</Button>
        </div>
      )}

      <div className="relative min-h-0 flex-1">
        <MindMapCanvas
          ref={canvasRef}
          nodes={nodes}
          shapes={shapes}
          sizes={editor.sizes}
          selectedId={selectedId}
          selectedShapeId={selectedShapeId}
          editingId={inlineEditingId}
          editingShapeId={editingShapeId}
          onSelect={select}
          onEdit={openEditor}
          onToggleCollapse={toggleCollapsed}
          onAddChild={addChild}
          onTextChange={updateNode}
          onCommitEdit={closeEditor}
          onBeginMove={editor.beginMove}
          onMoveNodes={editor.moveNodes}
          onSize={editor.reportSize}
          onSelectShape={selectShape}
          onEditShape={openShapeEditor}
          onShapeChange={editor.editShape}
          onMoveShape={editor.moveShape}
          onToggleShapeItem={editor.toggleShapeItem}
        />

        {panelNode ? (
          <NodeEditor
            node={panelNode}
            isRoot={panelNode.parentId == null}
            onChange={(patch) => updateNode(panelNode.id, patch)}
            onColor={(color) => setNodeColor(panelNode.id, color)}
            onToggleDone={() => toggleDone(panelNode.id)}
            onClose={() => closeEditor()}
          />
        ) : inlineEditingId || editingShapeId ? null : selectedShape ? (
          <ShapeToolbar
            shape={selectedShape}
            onEdit={() => openShapeEditor(selectedShape.id)}
            onKind={(kind) => setShapeKind(selectedShape.id, kind)}
            onFill={(fill) => updateShape(selectedShape.id, { fill })}
            onStroke={(stroke) => updateShape(selectedShape.id, { stroke })}
            onDelete={() => deleteShape(selectedShape.id)}
          />
        ) : selectedNode ? (
          <MindMapToolbar
            node={selectedNode}
            isRoot={selectedNode.parentId == null}
            hasChildren={!!hasChildren}
            onAddChild={() => addChild(selectedNode.id)}
            onAddSibling={() => addSibling(selectedNode.id)}
            onEdit={() => openEditor(selectedNode.id, 'panel')}
            onToggleDone={() => toggleDone(selectedNode.id)}
            onToggleCollapse={() => toggleCollapsed(selectedNode.id)}
            onDelete={() => deleteNode(selectedNode.id)}
          />
        ) : (
          <>
            {nodes.length <= 1 && shapes.length === 0 && (
              <p className="pointer-events-none absolute inset-x-0 bottom-24 px-6 text-center text-sm text-muted-foreground md:bottom-6">
                Tap an idea to select it, double-tap to rename it. Drag to move, pinch to zoom.
              </p>
            )}
            <div className="absolute bottom-0 right-0 z-10 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <Button
                variant="outline"
                onClick={() => addShape(canvasRef.current.viewportCenter())}
                className="h-12 gap-2 rounded-2xl bg-background/95 px-4 shadow-lg backdrop-blur"
              >
                <StickyNote className="h-5 w-5" />
                Add note
              </Button>
            </div>
          </>
        )}

        <div className="absolute bottom-4 left-4 z-10 hidden flex-col gap-1 rounded-xl border border-border bg-background/95 p-1 shadow-md md:flex">
          <IconButton icon={ZoomIn} label="Zoom in" onClick={() => canvasRef.current?.zoomBy(1.25)} />
          <IconButton icon={ZoomOut} label="Zoom out" onClick={() => canvasRef.current?.zoomBy(0.8)} />
        </div>
      </div>
    </div>
  );
}
